import Head from 'next/head'
import Image from 'next/image'
import { useState, useEffect } from 'react'
import { useViewport } from 'contexts/viewport'
import { useRouter } from 'next/router'
import axios from 'axios'

import AppHeader from 'components/app_header'
import AppFooter from 'components/app_footer'
import Link from 'next/link'
import IconArrowNext from 'components/icon/arrow_next'
import dayjs from 'utils/dayjs'
import {
  TeamGamesMetaDescription,
  TeamGamesOGImagePath,
  wpRoutePrefix,
  wpTagIdBySlug,
  blogDefaultFeaturedImage,
} from 'utils/constants'

import styles from 'styles/blogPost.module.scss'

export const WpPost = ({ posts, wpTag, authors, wpTagIds, postsMedia }) => {
  const router = useRouter()
  const { isMobile } = useViewport()
  const [wpPosts, setWpPosts] = useState(posts)
  const [wpAuthors, setWpAuthors] = useState(authors)
  const [wpMedia, setWpMedia] = useState(postsMedia)
  const [loadMore, setLoadMore] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPage, setTotalPage] = useState(2)
  const [isLoading, setIsLoading] = useState(false)
  const [keySearch, setKeySearch] = useState('')
  const firstPostId = posts.length > 0 ? posts[0]['id'] : 0

  useEffect(() => {
    const handleScrollLoadMorePost = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        if (page <= totalPage && loadMore) {
          setLoadMore(false)
          getPostData(page)
        }
      }
    }
    window.addEventListener('scroll', handleScrollLoadMorePost)
    return () => window.removeEventListener('scroll', handleScrollLoadMorePost)
  }, [loadMore, totalPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const getPostData = async page => {
    setLoadMore(false)
    setIsLoading(true)
    await axios
      .get(
        `https://wp.boom.tv/index.php/wp-json/wp/v2/posts?categories=14&tags=${wpTagIds}&exclude=${posts[0]['id']}&page=${page}&per_page=1`,
      )
      .then(response => {
        setIsLoading(false)
        setPage(page + 1)
        const posts = response.data
        setTotalPage(parseInt(response.headers['x-wp-totalpages']))

        if (posts.length > 0) {
          setWpPosts(post => [...post, ...posts])
          router.query.slug = posts[0].slug
          router.push(router, undefined, { shallow: true })
          if (!authors[posts[0].author]) {
            fetch(`https://wp.boom.tv/index.php/wp-json/wp/v2/users?include=${authors[posts[0].author]}`)
              .then(response => response.json())
              .then(result => {
                const newAuthor = {}
                newAuthor[result[0].id] = result[0]
                setWpAuthors(Object.assign(wpAuthors, newAuthor))
              })
          }
          if (posts[0].featured_media !== 0) {
            fetch(`https://wp.boom.tv/index.php/wp-json/wp/v2/media/${posts[0]['featured_media']}`)
              .then(response => response.json())
              .then(result => {
                const newMedia = {}
                newMedia[posts[0].id] = result.guid.rendered
                setWpMedia(Object.assign(wpMedia, newMedia))
              })
          } else {
            const newMedia = {}
            newMedia[posts[0].id] =
              typeof blogDefaultFeaturedImage[wpTag] !== 'undefined' ? blogDefaultFeaturedImage[wpTag] : ''
            setWpMedia(Object.assign(wpMedia, newMedia))
          }
          setTimeout(function () {
            setLoadMore(true)
          }, 500)
        }
      })
      .catch(error => {
        setIsLoading(false)
        console.log(error)
      })
  }

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  const handleChangeKeySearch = (event: { target: { value: any } }) => {
    const { value } = event.target
    setKeySearch(value.trim())
  }

  const handleSearch = () => {
    if (!keySearch) return
    location.href = '/help-details/search?q=' + keySearch
  }

  return (
    <div id="wpBlog_page" className={styles.news_page}>
      <Head>
        <title>{`${wpPosts[0].title.rendered.replace(/(<([^>]+)>)/gi, '')} | Team Games Blogs`}</title>
        <link rel="icon" href="/images/modal_logo.png" />
        <meta name="description" content={TeamGamesMetaDescription} />
        <meta name="og:image" content={TeamGamesOGImagePath} />
      </Head>
      <div className={styles.header_section}>
        <AppHeader transparent />
        <div className={styles.header_bg + (firstPostId && postsMedia[firstPostId] ? ' ' + styles.header_bg_ep : '')}>
          {isMobile ? (
            <div className={styles.header_bg_img}>
              <Image src="/images/wpTag/background_mobile.png" alt="Game for Every Team" layout="fill" />
            </div>
          ) : (
            <div className={styles.header_bg_img}>
              <Image src="/images/wpTag/background.png" alt="Game for Every Team" layout="fill" />
            </div>
          )}
        </div>
        <div className={styles.header_container}>
          <div className={styles.header_left}>
            <div className={styles.header_breadcrumb}>
              <div className={styles.header_help}>
                <Link href="/help-details">
                  <a>Help & Details</a>
                </Link>
              </div>
              <IconArrowNext />
              <div className={styles.header_wp_tag}>{wpTag.replace('-', ' ')}</div>
            </div>
          </div>
          <div className={styles.header_right}>
            <div className={styles.header_search_container}>
              {!isMobile && (
                <div className={styles.header_search_icon}>
                  <Image
                    src="/images/help_details/search_icon.png"
                    alt="Search Icon"
                    width={17}
                    height={17}
                    onClick={handleSearch}
                  />
                </div>
              )}
              <div className={styles.header_search_border} />
              <input
                className={styles.header_search_input}
                placeholder="Search FAQ, Announcements, etc..."
                onKeyDown={handleKeyDown}
                onChange={handleChangeKeySearch}
              />
              {isMobile && (
                <div className={styles.header_search_icon_mobile}>
                  <Image
                    src="/images/help_details/search_icon_mobile.png"
                    alt="Search Icon"
                    layout="fill"
                    onClick={handleSearch}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.container + (firstPostId && postsMedia[firstPostId] ? ' ' + styles.container_bg_ep : '')}>
        {/*eslint-disable @next/next/no-img-element*/}
        {wpPosts.map((post, postIndex) => (
          <div key={postIndex} className={styles.post_container}>
            <div className={styles.post}>
              {wpMedia[post.id] && (
                <img className={styles.post_media} src={wpMedia[post.id]} alt="Post Media" width={1400} height={652} />
              )}
              <div className={styles.main_content}>
                <div className={styles.main_tag}>
                  <Link href={`${wpRoutePrefix}/${wpTag}`}>
                    <a className={`heading_2 ${styles.tags}`}>{wpTag.replace('-', ' ')}</a>
                  </Link>
                </div>
                <div
                  className={styles.post_title + ' hero'}
                  dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                />
                <div className={styles.post_body}>
                  <div className={styles.post_author}>
                    <img
                      className={styles.post_author_avatar}
                      src={wpAuthors[post.author].avatar_urls[96]}
                      width={56}
                      height={56}
                      alt="Author Avatar"
                    />
                    <div className="heading_3">{wpAuthors[post.author].name}</div>
                    <div className={'heading_4 ' + styles.post_time}>
                      {dayjs(new Date(post.date)).format('MM/DD/YYYY')}
                    </div>
                  </div>
                  <div className={styles.post_content}>
                    <div className="description_medium" dangerouslySetInnerHTML={{ __html: post.content.rendered }} />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.author_section}>
              <div className={styles.author_section_title + ' heading_2'}>The Author</div>
              <div className={styles.author_section_info}>
                <img
                  className={styles.author_section_avatar}
                  src={wpAuthors[post.author].avatar_urls[96]}
                  width={75}
                  height={75}
                  alt="Author Avatar"
                />
                <div className={styles.author_section_about}>
                  <div className={styles.author_section_name + ' heading_3'}>{wpAuthors[post.author].name}</div>
                  <div className={styles.author_section_desc + ' description_small'}>
                    {wpAuthors[post.author].description}
                  </div>
                </div>
              </div>
            </div>
            {isLoading && (
              <div className={styles.loading_post}>
                <Image src="/images/ellipsis.svg" alt="Loading" layout="fill" />
              </div>
            )}
          </div>
        ))}
      </div>
      <AppFooter />
    </div>
  )
}

export async function getStaticPaths() {
  // Get first 5 news posts, per tag hardcoded below as they are used in /help-details.
  // Any paths not returned will be generated with SSR instead of static generation and then cached for future requests.
  const paths = []
  for (const wpTag in wpTagIdBySlug) {
    const postsResp = await axios.get(
      `https://wp.boom.tv/index.php/wp-json/wp/v2/posts?categories=14&tags=${wpTagIdBySlug[wpTag]}&per_page=5`,
    )
    const posts = postsResp.data
    // Get the paths we want to pre-render based on posts
    const newPaths = posts.map((post: { slug: string }) => ({
      params: { wpTag, slug: post.slug },
    }))
    paths.push(...newPaths)
  }
  // console.log('paths:', paths)

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return {
    paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  const wpTag = params.wpTag
  let wpTagIds: string = wpTagIdBySlug[wpTag]
  if (!wpTagIds) {
    const tagResp = await axios.get(`https://wp.boom.tv/index.php/wp-json/wp/v2/tags?slug=${wpTag}`)
    const tagData = tagResp.data?.[0]
    if (tagData) wpTagIds = tagData.id.toString()
    else
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      }
  }

  // Retrieve one first post
  const postsResp = await axios.get(
    `https://wp.boom.tv/index.php/wp-json/wp/v2/posts?categories=14&slug=${params.slug}&page=1&per_page=1`,
  )
  const posts = postsResp.data
  const post = posts[0]

  // Retrieve media and build a map for easy author lookup
  const postsMedia = {}
  if (post['featured_media'] !== 0) {
    const mediaResp = await axios.get(`https://wp.boom.tv/index.php/wp-json/wp/v2/media/${post['featured_media']}`)
    postsMedia[post.id] = mediaResp.data.guid.rendered
  } else {
    postsMedia[post.id] = typeof blogDefaultFeaturedImage[wpTag] !== 'undefined' ? blogDefaultFeaturedImage[wpTag] : ''
  }

  // Retrieve authors and build a map for easy author lookup
  const authorsResp = await axios.get(
    `https://wp.boom.tv/index.php/wp-json/wp/v2/users?include=${post['author']}&per_page=1`,
  )
  const authors = {}
  for (const author of authorsResp.data) {
    authors[author.id] = author
  }
  return {
    props: {
      posts,
      wpTag,
      authors,
      wpTagIds,
      postsMedia,
    },
    revalidate: 30,
  }
}

export default WpPost
